import { Component, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { PolicySelectors, PolicySummarySelectors } from '@amfam/policy/data-access';
import {
  GenericProductType,
  Policy,
  PolicyAnalytics,
  PolicySummary,
  PolicyTypeDisplayNameConstants,
  PolicyTypeIconConstants,
  RiskModel
} from '@amfam/policy/models';
import { VechiclePolicyChangeEntryPoint, VehicleFacade } from '@amfam/policy/vehicle/data-access';
import { profileQuery } from '@amfam/profile/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { DocumentActions, OverviewAnalytics } from '@amfam/shared/models';
import { DocumentPipeService } from '@amfam/shared/ui/pipes';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import {
  CopyService,
  EventDispatcherService,
  openWindowBlob,
  policyOrder,
  WindowRef
} from '@amfam/shared/utility/shared-services';
import { DsModalService, LoadingSpinnerService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';

@Component({
  selector: 'policies-overview',
  templateUrl: './policies-overview.component.html',
  styleUrls: ['./policies-overview.component.scss', '../overview.component.scss'],
  standalone: false
})
export class PoliciesOverviewComponent implements OnInit, OnDestroy {
  risks = new Array<RiskModel>();
  vehicleRisks = new Array<RiskModel>();
  homeRisks = new Array<RiskModel>();
  umbrellaRisks = new Array<RiskModel>();
  allOtherRisks = new Array<RiskModel>();
  lifeRisks = new Array<RiskModel>();
  clickLifePolicyAnalytics = OverviewAnalytics.clickLifePolicy;
  homesitePolicies = [];
  homesitePolicyTypeMap: Map<string, string>;
  externalPropertyUrl: string;
  amfamDotComUrl: string;
  dataLoading$: Observable<boolean>;
  dataLoaded: Observable<boolean>;
  hasSecurityQuestions: Observable<boolean>;
  isCustomer: Observable<boolean>;
  policyError: Observable<boolean>;
  activeAutoPolicies$ = this.store.select(fromRoot.getActiveScheduledAutoPolicies);
  showModal = false;
  addVehicleEnabled$ = this.store.select(fromRoot.getAddVehicleEnabled);
  partnerAddVehicleEnabled$ = this.store.select(fromRoot.partnerAddVehicleEnabled);
  isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  policyUnavailableModalId = 'policyUnavailableModal';
  toolTipContent: { header: string; toolTip: string };
  genericProductType = GenericProductType;
  forgerockEnabled = this.featureFlagService.isEnabled('forgerock');
  userAccountExpApiEnabled = this.featureFlagService.isEnabled('user_account_experience_api');

  private stop$ = new Subject<void>();

  constructor(
    private featureFlagService: FeatureFlagService,
    private store: Store<fromRoot.RootState>,
    private spinner: LoadingSpinnerService,
    private win: WindowRef,
    private sanitizer: DomSanitizer,
    private documentPipeService: DocumentPipeService,
    private vehicleFacade: VehicleFacade,
    private analyticsFacade: AnalyticsFacade,
    private deviceService: DeviceDetectorService,
    private modalService: DsModalService,
    private copyService: CopyService,
    private eventDispatcherService: EventDispatcherService
  ) {
    this.amfamDotComUrl = env.links.amfamDotComUrl;
  }

  ngOnInit() {
    this.getToolTipContent();
    this.dataLoading$ = combineLatest([
      this.store.select(PolicySelectors.selectPoliciesLoading),
      this.store.select(PolicySummarySelectors.selectPolicySummaryLoading)
    ]).pipe(map(([detailLoading, summaryLoading]) => detailLoading || summaryLoading));

    this.dataLoaded = this.store.select(PolicySelectors.selectPoliciesLoaded);
    this.hasSecurityQuestions = this.store.select(profileQuery.selectHasSecurityQuestions);
    this.isCustomer = this.store.select(userQuery.selectIsCustomer);

    this.policyError = this.store.select(PolicySummarySelectors.selectPolicySummariesHasError);

    this.setPolicyTypeCodes();

    combineLatest([
      this.store.select(PolicySummarySelectors.selectAllPolicySummaries),
      this.store.select(PolicySelectors.selectPolicyRisks),
      this.store.select(PolicySummarySelectors.selectHomesitePolicies),
      this.store.select(BrandSelectors.selectPropertyServiceUrl),
      this.store.select(PolicySelectors.selectInactivePolicies),
      this.dataLoading$
    ])
      .pipe(
        map(([summaries, risks, homesitePolicies, url, inactivePolicies, dataLoading]) => ({
          summaries,
          risks,
          homesitePolicies,
          url,
          inactivePolicies,
          loading: dataLoading
        })),
        takeUntil(this.stop$),
        filter(state => !state.loading)
      )
      .subscribe(state => {
        if (this.featureFlagService.isEnabled('user_account_experience_api')) {
          this.risks = [
            ...this.mapPoliciesFromUserAPI(state.summaries, state.risks, state.inactivePolicies)
          ];
        } else {
          this.risks = [...this.mapPolicies(state.summaries, state.risks, state.inactivePolicies)];
        }

        this.vehicleRisks = this.risks.filter(
          risk => risk.policyTypeDisplayName === PolicyTypeDisplayNameConstants.AUTO
        );
        this.homeRisks = this.risks.filter(
          risk => risk.policyTypeDisplayName === PolicyTypeDisplayNameConstants.HOME
        );
        this.umbrellaRisks = this.risks.filter(
          risk => risk.policyTypeDisplayName === PolicyTypeDisplayNameConstants.UMBRELLA
        );
        this.lifeRisks = this.risks.filter(
          risk => risk.policyTypeDisplayName === PolicyTypeDisplayNameConstants.LIFE
        );
        this.allOtherRisks = this.risks.filter(
          risk =>
            risk.policyTypeDisplayName !== PolicyTypeDisplayNameConstants.AUTO &&
            risk.policyTypeDisplayName !== PolicyTypeDisplayNameConstants.HOME &&
            risk.policyTypeDisplayName !== PolicyTypeDisplayNameConstants.UMBRELLA &&
            risk.policyTypeDisplayName !== PolicyTypeDisplayNameConstants.LIFE
        );
        this.homesitePolicies = state.homesitePolicies;
        this.externalPropertyUrl = state.url;
      });
  }

  mapPolicies(summaries: PolicySummary[], risks: RiskModel[], inactivePolicies: Policy[]) {
    return summaries
      .filter(summary => summary.sourcePath !== 'HOMESITE')
      .filter(summary => !inactivePolicies.some(p => p.policyNumber === summary.policyNumber))
      .map(summary => {
        const filteredRisks = risks.filter(r => r.policyNumber === summary.policyNumber);
        if (filteredRisks.length > 0) return filteredRisks;
        else {
          const missedPolicy: RiskModel = this.getEmptyRisk();
          missedPolicy.policyNumber = summary.policyNumber;
          missedPolicy.fromSummary = true;
          missedPolicy.description = summary.policyNumber;
          missedPolicy.policyTypeDisplayName = summary.policyTypeDisplayName;
          missedPolicy.iconType = this.getIconType(
            summary.policyTypeDisplayName,
            summary.productDescription
          );
          missedPolicy.sortType = summary.sortType;
          missedPolicy.operator = !!summary.operator;
          missedPolicy.poiLink = summary.poiLink;
          missedPolicy.generalizedProductType = summary.generalizedProductType;

          if (!!summary.operator) {
            const risks = summary.risk;
            if (risks?.length > 0) {
              return risks.map(risk => {
                if (risk.riskDescription === 'Multiple Risks') {
                  const descriptiion = `Policy ${summary.policyNumber}`;
                  return { ...missedPolicy, description: descriptiion } as RiskModel;
                } else {
                  const descriptiion = risk.riskDescription
                    ? risk.riskDescription.replace(/;/g, ' ')
                    : missedPolicy.description;
                  return { ...missedPolicy, description: descriptiion } as RiskModel;
                }
              });
            }
          }
          return missedPolicy;
        }
      })
      .flat()
      .sort((a, b) => policyOrder.indexOf(a.sortType) - policyOrder.indexOf(b.sortType));
  }

  mapPoliciesFromUserAPI(
    summaries: PolicySummary[],
    risks: RiskModel[],
    inactivePolicies: Policy[]
  ) {
    return summaries
      .filter(summary => !inactivePolicies.some(p => p.policyNumber === summary.policyNumber))
      .map(summary => {
        const filteredRisks = risks.filter(r => r.policyNumber === summary.policyNumber);
        if (filteredRisks.length > 0) return filteredRisks;
        else {
          // When the details call fail or details call does not have the policy that is in summary or when it is operator only
          const missedPolicy: RiskModel = this.getEmptyRisk();
          missedPolicy.policyNumber = summary.policyNumber;
          missedPolicy.fromSummary = true;
          missedPolicy.description = summary.policyNumber;
          missedPolicy.policyTypeDisplayName = summary.policyTypeDisplayName;
          missedPolicy.iconType = this.getIconType(
            summary.policyTypeDisplayName,
            summary.productDescription
          );

          missedPolicy.operator = !!summary.operator;
          missedPolicy.poiLink = summary.poiLink;
          missedPolicy.generalizedProductType = summary.generalizedProductType;

          if (!!summary.operator) {
            const summaryRisks = summary.summaryRisk;
            if (summaryRisks?.length > 1) {
              missedPolicy.description = summary.policyNumber;
            }
            if (summaryRisks?.length === 1) {
              missedPolicy.description = summaryRisks[0];
            }
          }
          return missedPolicy;
        }
      })
      .flat();
  }

  getEmptyRisk(): RiskModel {
    return {
      generalizedProductType: null,
      hasExistingDevice: null,
      iconType: null,
      id: null,
      make: null,
      model: null,
      periodEndDate: null,
      periodStartDate: null,
      cancelDate: null,
      poiLink: null,
      policy: null,
      policyNumber: null,
      policyType: null,
      policyTypeDisplayName: null,
      policyTypeName: null,
      previouslyEnrolled: null,
      risk: null,
      vehicleCoverages: null,
      vehicleDiscounts: null,
      vehicleFees: null,
      vehicleRoles: null,
      vehicleTypeName: null,
      vin: null,
      year: null,
      fromSummary: null
    };
  }

  getIconType(value: string, description: string): string {
    const key: string = Object.keys(PolicyTypeDisplayNameConstants).find(
      keyValue => PolicyTypeDisplayNameConstants[keyValue] === value
    );
    if (PolicyTypeDisplayNameConstants[key] === 'Auto') {
      // when cycle, rv and watercraft
      const icon = PolicyTypeIconConstants[description?.toUpperCase().trim().replace(/\s/g, '')];
      if (icon) return icon;
    }

    return PolicyTypeIconConstants[key];
  }

  getToolTipContent() {
    this.toolTipContent = this.copyService.getCopy('policy.operatorToolTipContent');
  }

  setPolicyTypeCodes() {
    this.homesitePolicyTypeMap = new Map()
      .set('HO3', 'Homeowners')
      .set('HO4', 'Renters')
      .set('HO6', 'Condo')
      .set('HF9', 'Second Home');
  }

  openModal() {
    this.showModal = true;
  }
  public openStatementOnAndroid(link: string) {
    let windowRef;
    if (!window.navigator) {
      windowRef = this.win.nativeWindow.open();
    }
    this.documentPipeService
      .retrieveDocument(link)
      .pipe(take(1))
      .subscribe(
        res => {
          if (!res) {
            if (windowRef) {
              windowRef.close();
            }
            this.analyticsFacade.trackPage(OverviewAnalytics.overviewGetPOIError);
          }
          const url = this.sanitizer.sanitize(SecurityContext.URL, res);
          this.spinner.stop();
          openWindowBlob(this.win, url, res, windowRef, 'document.pdf');
        },
        err => {
          if (windowRef) {
            windowRef.close();
          }
        }
      );
  }

  public onGetInsuranceCardClick(risk: RiskModel): void {
    if (risk.operator) {
      this.analyticsFacade.trackButtonClick(OverviewAnalytics.overviewGetOperatorPOIButton);
    } else {
      this.analyticsFacade.trackButtonClick(OverviewAnalytics.overviewGetPOIButton);
    }
    if (risk.poiLink) this.openStatement(risk.poiLink);
    else this.eventDispatcherService.openNoDocumentModal(DocumentActions.DOWNLOAD);
  }

  public openStatement(link: string) {
    this.spinner.start();
    if (this.deviceService.device.toLowerCase() === 'android') this.openStatementOnAndroid(link);
    else {
      let windowRef;
      windowRef = this.win.nativeWindow.open();
      this.documentPipeService
        .retrieveDocument(link)
        .pipe(take(1))
        .subscribe(
          res => {
            if (!res) {
              if (windowRef) {
                windowRef.close();
              }
              this.analyticsFacade.trackPage(OverviewAnalytics.overviewGetPOIError);
            }
            const url = this.sanitizer.sanitize(SecurityContext.URL, res);
            this.spinner.stop();
            windowRef.location.href = url;
          },
          err => {
            if (windowRef) {
              windowRef.close();
            }
          }
        );
    }
  }

  public routeToAddVehicle() {
    this.vehicleFacade.setVehiclePolicyChangeEntryPoint(VechiclePolicyChangeEntryPoint.OVERVIEW);

    combineLatest([
      this.store.select(fromRoot.hasAnyPolicyCompAndCollisonCoverageAndCarLightTruck),
      this.store.select(BrandSelectors.selectIsPartner),
      this.store.select(PolicySelectors.selectHasClassicPolicies),
      this.store.select(PolicySelectors.selectHasAdvancePolicies),
      this.store.select(PolicySelectors.selectHasOnlyPolicyComprehensiveCoverage),
      this.store.select(fromRoot.hasAnyPolicyLiabilityCoverage)
    ])
      .pipe(
        map(
          ([
            isEligibleForAddVehicleFlow,
            isPartner,
            hasClassicPolicy,
            advancePolicy,
            hasOnlyPolicyComprehensiveCoverage,
            hasAnyPolicyLiabilityCoverage
          ]) => ({
            isEligibleForAddVehicleFlow,
            isPartner,
            hasClassicPolicy,
            advancePolicy,
            hasOnlyPolicyComprehensiveCoverage,
            hasAnyPolicyLiabilityCoverage
          })
        ),
        take(1)
      )
      .subscribe(
        ({
          isEligibleForAddVehicleFlow,
          isPartner,
          hasClassicPolicy,
          advancePolicy,
          hasOnlyPolicyComprehensiveCoverage,
          hasAnyPolicyLiabilityCoverage
        }) => {
          let addVehicleRoute;
          const addVehicleSkipHardstop = this.featureFlagService.isEnabled(
            'add_vehicle_skip_hardstop'
          );
          const isShowhardstopRiskSelectionPage =
            this.featureFlagService.isEnabled('add_vehicle') &&
            addVehicleSkipHardstop &&
            ((hasClassicPolicy && !advancePolicy && !isPartner) ||
              (advancePolicy && !isPartner && hasOnlyPolicyComprehensiveCoverage) ||
              hasAnyPolicyLiabilityCoverage);

          if (
            isPartner &&
            this.featureFlagService.isEnabled('add_vehicle') &&
            isEligibleForAddVehicleFlow
          ) {
            addVehicleRoute = ['/policies/add-vehicle', 'expectation'];
          } else if (
            (this.featureFlagService.isEnabled('add_vehicle') && isEligibleForAddVehicleFlow) ||
            isShowhardstopRiskSelectionPage
          ) {
            addVehicleRoute = ['/policies/risk-selection'];
          } else if (isPartner) {
            addVehicleRoute = ['/policies/add-vehicle/agent-help'];
          } else {
            addVehicleRoute = ['/policies/risk-selection', 'grace-period'];
          }

          this.store.dispatch(
            fromRouterActions.Go({
              path: addVehicleRoute
            })
          );
        }
      );
  }

  openPolicyUnavailableModal() {
    this.modalService.open(this.policyUnavailableModalId);
  }

  closePolicyUnavailableModal() {
    this.modalService.close(this.policyUnavailableModalId);
  }
  sendPOIButtonAnalytics() {
    this.analyticsFacade.trackButtonClick(PolicyAnalytics.buttonOverviewGetPOI);
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
